import { deburr, toUpper, every, get, isNil, map, omitBy, some, toString } from 'lodash/fp';

import { makeInitials, makeFirstNameInitials } from '../utils/playerUtils';

const isActive = some(x => x.name === 'C' || x.name === 'S');
const isScratched = some(x => x.name === 'SCR');
const isIncluded = every(x => ['O', 'SCR', '\u2716'].includes(x.name) === false);
const isQuestionable = some(x =>
  ['O', 'E', 'R', 'GTD', 'PUP', 'Q', 'D', 'IR', 'SCR', '\u2716'].includes(x.name)
);
export const isReserved = some(x => ['O', 'E', 'R', 'PUP', 'IR', 'SCR', '\u2716'].includes(x.name));
const omitNil = omitBy(isNil);

const BASKETBALL_SPORT_ID = 7;

function decodePlayer(player = {}, playerGame) {
  const game = playerGame || get('game', player);
  const countryCode = get('competition.competition_type.country.iso_alpha_2_code', game);
  const hideProgress = !!get('competition.competition_type.sport.game_progress_irrelevant', game);

  const labels = map(
    item => ({
      bgColor: item.bg_color,
      id: item.name,
      name: item.name,
      textColor: item.text_color
    }),
    player.labels
  );
  const homeId = get('home_team.id', game) || get('home_team_id', game);
  const { team } = player;

  let preferredName =
    get('competition.competition_type.sport.id', game) === BASKETBALL_SPORT_ID
      ? `${player.first_name} ${player.last_name}`
      : player.preferred_name;

  if (typeof preferredName === 'string' && typeof preferredName.normalize === 'function') {
    preferredName = preferredName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  const gamePlaced = get('meta.game_has_placed', player);
  const rawForm = get('form', player.meta);
  const dualAccepted = get('meta.dual_accepted', player);
  const positions = player.positions || [];
  const barrier = get('meta.barrier', player);
  const trainer = get('meta.trainer.name', player);
  const rider = get('meta.rider.name', player);
  const riderImage = get('meta.rider.image', player);
  const form = rawForm
    ? rawForm.substring(rawForm.length - 5)
    : parseFloat(get('three_game_average', player));
  const place = get('meta.scores.place', player) || 0;
  const placeProjected = get('meta.projected_scores.place', player) || 0;
  const win = get('meta.scores.win', player) || 0;
  const winProjected = get('meta.projected_scores.win', player) || 0;

  const isRacing = barrier || rider || trainer;
  let name = isRacing ? `${player.number}. ${preferredName}` : preferredName;

  if (barrier) {
    name += ` (${barrier})`;
  }
  return omitNil({
    active: isActive(labels),
    average: parseFloat(get('average_onescore', player)) || 0,
    awayAbbr:
      get('away_team.abbreviation', game) ||
      get('away_team_abbreviation', game) ||
      get('away_team.name', game) ||
      get('away_team_name', game),
    awayId: get('away_team.id', game) || get('away_team_id', game),
    awayName: get('away_team.name', game) || get('away_team_name', game),
    barrier,
    dualAccepted,
    bonus: get('bonus', player),
    dropped: get('dropped', player),
    form: form || 0,
    gameId: game ? toString(get('id', game)) : undefined,
    gameNumber: get('number', game) || get('meta.number', game),
    gameOrder: get('gameOrder', player) || get('gameOrder', game),
    gamePlaced,
    gameStage: get('status.name', game),
    gameStartTime: new Date(get('start_time', game)).getTime(),
    gameVenue: get('venue', game),
    homeAbbr:
      get('home_team.abbreviation', game) ||
      get('home_team_abbreviation', game) ||
      get('home_team.name', game) ||
      get('home_team_name', game),
    homeId,
    homeName: get('home_team.name', game) || get('home_team_name', game),
    id: toString(player.id),
    idStr: `PLAYER_${player.id}`,
    imageURI: player.image || get('image', team),
    included: isIncluded(labels),
    initials: makeInitials(preferredName),
    isHome: get('is_home_team', team) || get('id', team) === homeId,
    isLocked: get('is_locked', game),
    isQuestionable: isQuestionable(labels),
    isReserved: isReserved(labels),
    isScratched: isScratched(labels),
    isTeam: false,
    labels,
    name,
    nameLookup: toUpper(deburr(preferredName)),
    number: player.number,
    place,
    placeProjected: gamePlaced ? place : placeProjected,
    preferredName,
    popularity:
      player.selected_percentage !== null ? parseInt(player.selected_percentage, 10) : null,
    positions,
    positionId: get('key', player.position), // live game only
    positionAbbr:
      get('short_label', player.position) ||
      positions.map(pos => get('short_label', pos)).join(' | '), // live game only
    positionName: get('label', player.position), // live game only
    progress: !hideProgress ? get('progress', game) : undefined,
    reverseOrder: countryCode ? countryCode === 'US' : undefined,
    rider: rider ? makeFirstNameInitials(rider) : undefined,
    riderImage,
    salary: player.salary,
    score: get('fantasy_score', player) || 0,
    scoreProjected: get('projected_score', player) || 0,
    statusId: get('status.id', game),
    statusName: get('status.name', game),
    teamId: toString(get('id', team)),
    teamAbbr: get('abbreviation', team),
    teamImage: get('image', team),
    teamName: get('name', team),
    trainer: trainer ? makeFirstNameInitials(trainer) : undefined,
    weight: player.weight,
    win,
    winProjected: gamePlaced ? win : winProjected
  });
}

export default decodePlayer;
